import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import posthog, { isPostHogConfigured } from "../../src/posthog";
import FoodScannerPage from "../pages/food-scanner.page";
import { Auth0Service, type AuthSession } from "../services/auth0.service";
import type { SupabaseAuthSession } from "../services/supabase-auth.service";
import {
	getSupabaseSession,
	onAuthStateChange,
	signOut as supabaseSignOut,
} from "../services/supabase-auth.service";
import { LoginPanel } from "./auth/login-panel";
import { SignupPanel } from "./auth/signup-panel";

const auth0Config = {
	domain: import.meta.env.VITE_AUTH0_DOMAIN || "",
	clientId: import.meta.env.VITE_AUTH0_CLIENT_ID || "",
	audience: import.meta.env.VITE_AUTH0_AUDIENCE || "",
};

const supabaseConfigured = Boolean(
	import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY,
);
const auth0Configured = Object.values(auth0Config).every(Boolean);

function usePostHogIdentity(userId: string | null) {
	const identifiedId = useRef<string | null>(null);

	useEffect(() => {
		if (!isPostHogConfigured) return;
		if (!userId) {
			if (identifiedId.current) posthog.reset();
			identifiedId.current = null;
			return;
		}
		if (identifiedId.current === userId) return;
		if (identifiedId.current) posthog.reset();

		posthog.identify(userId);
		identifiedId.current = userId;
	}, [userId]);

	return useCallback(() => {
		if (!isPostHogConfigured || !identifiedId.current) return;
		posthog.reset();
		identifiedId.current = null;
	}, []);
}

export function AuthenticatedApp() {
	// Supabase takes precedence if configured; otherwise fall back to Auth0 for backward compat
	if (supabaseConfigured) {
		return <SupabaseAuthenticatedApp />;
	}
	if (auth0Configured) {
		return <Auth0AuthenticatedApp />;
	}
	return (
		<div className="auth-screen">
			<div className="auth-card" role="alert">
				<span className="brand-mark">G</span>
				<h1>Chưa cấu hình đăng nhập</h1>
				<p>
					Hãy thiết lập <code>VITE_SUPABASE_URL</code> và{" "}
					<code>VITE_SUPABASE_ANON_KEY</code> (hoặc legacy{" "}
					<code>VITE_AUTH0_*</code>) trước khi chạy ứng dụng.
				</p>
			</div>
		</div>
	);
}

function SupabaseAuthenticatedApp() {
	const [session, setSession] = useState<SupabaseAuthSession | null>(null);
	const [loading, setLoading] = useState(true);
	const [mode, setMode] = useState<"login" | "signup">("login");
	const [error, setError] = useState("");
	const userName = session
		? [session.user.firstName, session.user.lastName]
				.filter(Boolean)
				.join(" ") || session.user.email
		: undefined;
	const resetPostHog = usePostHogIdentity(session?.user.id ?? null);

	useEffect(() => {
		getSupabaseSession()
			.then(setSession)
			.catch((cause: unknown) => {
				setError(
					cause instanceof Error ? cause.message : "Không thể khôi phục phiên.",
				);
			})
			.finally(() => setLoading(false));
		const {
			data: { subscription },
		} = onAuthStateChange(setSession);
		return () => subscription.unsubscribe();
	}, []);

	useEffect(() => {
		if (!session) return;
		const delay = Math.max(0, session.expiresAt - Date.now() - 60_000);
		const timer = window.setTimeout(() => {
			resetPostHog();
			setSession(null);
		}, delay);
		return () => window.clearTimeout(timer);
	}, [resetPostHog, session]);

	if (loading) {
		return (
			<div className="auth-screen" aria-live="polite">
				<div className="spinner" />
				<p>Đang kiểm tra phiên đăng nhập…</p>
			</div>
		);
	}

	if (!session) {
		return (
			<div className="auth-screen">
				{mode === "login" ? (
					<LoginPanel
						onSuccess={() => {}}
						onSwitchToSignup={() => setMode("signup")}
						error={error}
					/>
				) : (
					<SignupPanel
						onSuccess={() => setMode("login")}
						onSwitchToLogin={() => setMode("login")}
					/>
				)}
			</div>
		);
	}

	return (
		<FoodScannerPage
			accessToken={session.accessToken}
			user={{
				name: userName || "Tài khoản",
				email: session.user.email,
			}}
			onAuthenticationRequired={() => {
				resetPostHog();
				setError("Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.");
				setSession(null);
			}}
			onLogout={async () => {
				await supabaseSignOut();
				resetPostHog();
				setSession(null);
			}}
		/>
	);
}

function Auth0AuthenticatedApp() {
	const auth0 = useMemo(() => {
		try {
			return new Auth0Service(auth0Config);
		} catch {
			return null;
		}
	}, []);
	const [session, setSession] = useState<AuthSession | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const resetPostHog = usePostHogIdentity(session?.user.id ?? null);

	useEffect(() => {
		if (!auth0) {
			setLoading(false);
			return;
		}
		auth0
			.handleRedirectCallback()
			.then(setSession)
			.catch((cause: unknown) => {
				setError(
					cause instanceof Error
						? cause.message
						: "Không thể đăng nhập bằng Auth0.",
				);
			})
			.finally(() => setLoading(false));
	}, [auth0]);

	useEffect(() => {
		if (!session) return;
		const delay = Math.max(0, session.expiresAt - Date.now() - 60_000);
		const timer = window.setTimeout(() => {
			resetPostHog();
			setSession(null);
		}, delay);
		return () => window.clearTimeout(timer);
	}, [resetPostHog, session]);

	if (!auth0) {
		return (
			<div className="auth-screen">
				<div className="auth-card" role="alert">
					<span className="brand-mark">G</span>
					<h1>Auth0 chưa được cấu hình</h1>
					<p>
						Hãy thiết lập VITE_AUTH0_DOMAIN, VITE_AUTH0_CLIENT_ID và
						VITE_AUTH0_AUDIENCE trước khi chạy ứng dụng.
					</p>
				</div>
			</div>
		);
	}

	if (loading) {
		return (
			<div className="auth-screen" aria-live="polite">
				<div className="spinner" />
				<p>Đang kiểm tra phiên đăng nhập…</p>
			</div>
		);
	}

	if (!session) {
		return (
			<div className="auth-screen">
				<div className="auth-card">
					<span className="brand-mark">G</span>
					<p className="eyebrow">Gout Food Scanner</p>
					<h1>Đăng nhập để bắt đầu</h1>
					<p>
						Đăng nhập an toàn qua Auth0 để sử dụng tính năng phân tích thực
						phẩm.
					</p>
					{error && (
						<div className="error" role="alert">
							{error}
						</div>
					)}
					<button
						type="button"
						className="primary auth-button"
						onClick={() => {
							setError("");
							auth0?.login().catch((cause: unknown) => {
								setError(
									cause instanceof Error
										? cause.message
										: "Không thể mở trang đăng nhập Auth0.",
								);
							});
						}}
					>
						Đăng nhập với Auth0 <span>→</span>
					</button>
				</div>
			</div>
		);
	}

	return (
		<FoodScannerPage
			accessToken={session.accessToken}
			user={session.user}
			onAuthenticationRequired={() => {
				resetPostHog();
				auth0.clearSession();
				setSession(null);
				setError("Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.");
			}}
			onLogout={() => {
				resetPostHog();
				auth0.logout();
			}}
		/>
	);
}
